import * as IdbKeyVal from "idb-keyval";
import TagManager from 'react-gtm-module';
import axiosInstance from '../services/AxiosService';
import Golfer, { LoftForFitting, Loft, LoftShots, EnhancedFittingData } from '../classes/Golfer'
import * as ShotSelection from "../classes/ShotSelection";
import Fitter from '../classes/Fitter';
import * as TutorialHelper from './TutorialHelper';
import * as FitterHelper from './FitterHelper';
import SimulatorData from '../classes/SimulatorData';
import ISimulator from '../classes/ISimulator';
import { LoftGrindRecommendation } from "./GrindLookup";

const GOLFER_LIST = "golferList"
const GOLFER_ID = "golferId"

//create empty golfer object with unique Id
export const newGolfer = async (): Promise<Golfer> => {
    //get next Id in the queue
    let maxId = 0;
    const golferList = await getGolferListByFitter();
    golferList.forEach(g => {
        maxId = g.id > maxId ? g.id : maxId;
    })

    let golfer = new Golfer();
    golfer.id = maxId + 1;
    golfer.orderId = (window.crypto as any).randomUUID();
    return golfer;
}

export const getGolferListByFitter = async (fitterId = 0, fitterEmail = ""): Promise<Golfer[]> => {
    // numeric id for user pool 1 user and email for user pool 2 users
    const currentFitterId = await FitterHelper.getFitterId();
    let golfers = [] as Golfer[];
    const golferList = await IdbKeyVal.get<Golfer[]>(GOLFER_LIST);
    if (golferList) {
        golfers = golferList;
        if (fitterId !== 0 && fitterId !== 12006) {
            //for user pool 1, use the email address or their Id
            golfers = golfers.filter(g => g.fitterId === fitterId || g.fitterEmail === fitterEmail);
        } else {
            //for user pool 2 fitters, they all share the same id, use unique email only
            golfers = golfers.filter(g => g.fitterEmail === (fitterEmail || currentFitterId));
        }
    }
    //descending sort
    golfers.sort((a, b) => b.id - a.id);
    return golfers;
}

const getGolferList = async (): Promise<Golfer[] | undefined> => {
    const golferList = await IdbKeyVal.get<Golfer[]>(GOLFER_LIST);
    return golferList;
}

export const setGolferId = async (id: number) => {
    const response = await IdbKeyVal.set(GOLFER_ID, id);
}

export const getGolferId = async (): Promise<number> => {
    let golferId = 0;
    const response = await IdbKeyVal.get<number>(GOLFER_ID);
    if (response) {
        golferId = response;
    }
    return golferId;
}

export const getCurrentGolfer = async (): Promise<Golfer> => {
    //if the app is showing a tutorial, then return the Tutorial Golfer object
    let isTutorial = await TutorialHelper.isActive();
    if (isTutorial) {
        let golfer = await TutorialHelper.getGolfer();
        return golfer;
    }
    let golfers = await getGolferListByFitter();
    let golferId = await getGolferId();
    let golfer = golfers.filter(g => g.id === golferId);
    return golfer.length > 0 ? golfer[0] : {} as Golfer;
}

export const getGolferById = async (id: number): Promise<Golfer> => {
    let golfers = await getGolferListByFitter();
    let golfer = {} as Golfer;
    let g = golfers.filter(g => g.id === id);
    if (g.length > 0) {
        golfer = g[0];
    }
    return golfer;
}

export const saveGolferList = async (golfers: Golfer[]): Promise<void> => {
    await IdbKeyVal.set(GOLFER_LIST, golfers);
}

// Update the localDB with new golfer list, which represents the fittings performed in the app for a given device
export const upsertGolfer = async (currentGolfer: Golfer): Promise<void> => {
    const golferList = (await getGolferList()) || [];
    let updatedGolferList: Golfer[];
    // Create or Update golfer from saved list
    if (!golferList?.some((golfer) => golfer.orderId === currentGolfer.orderId)) {
        updatedGolferList = golferList;
        updatedGolferList.push(currentGolfer);
    } else {
        updatedGolferList = golferList.map((savedGolfer) => {
            if (savedGolfer.orderId === currentGolfer.orderId) {
                // Overwrite saved values with the current ones
                return { ...savedGolfer, ...currentGolfer };
            }
            return savedGolfer;
        });
    }
    // Save to local storage
    await saveGolferList(updatedGolferList);
    // Set the golfer id to the current golfer
    await IdbKeyVal.set(GOLFER_ID, currentGolfer.id);
}

//there are three rules applied for deciding which shots to have the golfer fitted for (see below)
export const calculateLoftsForFitting = (fitter: Fitter, loftList: LoftShots[], loftsForFitting: LoftForFitting[]): LoftForFitting[] => {

    //if fitting type is detailed, then add every shot for all selected lofts.
    if (fitter.fittingType === "Detailed") {
        loftList.forEach(loft => {
            loft.shots.forEach(shot => {
                addLoftToCollection(fitter.shotsPerClub, loft.lineNumber, loft.loft, shot.shotType, loftsForFitting);
            })
        })
    } else {
        //rule 0: Add the first selected shot type for each loft. Only get one shot type for each loft
        loftList.forEach(loft => {
            // Sort selected shots with highest priority first. Use first shot in the list
            let selectedShots = loft.shots.filter(l => l.selected).sort((a, b) => a.priority - b.priority);
            if (selectedShots.length > 0) {
                addLoftToCollection(fitter.shotsPerClub, loft.lineNumber, loft.loft, selectedShots[0].shotType, loftsForFitting);
            }
        });

        //rule 1: Need two smallest lofts for full shots. Do this even if there are already two Full shots selected and one of them is a high loft.
        let rule1Count = 0;
        loftList.forEach(loft => {
            if (rule1Count < 2 && loft.shots.filter(shot => shot.shotType === 'Full' && shot.selected).length > 0) {
                addLoftToCollection(fitter.shotsPerClub, loft.lineNumber, loft.loft, 'Full', loftsForFitting)
                rule1Count++;
            }
        });

        //rule 2: if a selected shot hasn't been added to the collection yet, then add it for the highest loft that uses it.
        //no way to do this smoothly b/c we can't iterate over a type declaration, just gotta do each shot type individually
        assignHighestLoftForShot(fitter, 'Full', loftsForFitting, loftList)
        assignHighestLoftForShot(fitter, 'Pitch', loftsForFitting, loftList)
        assignHighestLoftForShot(fitter, 'Open Face', loftsForFitting, loftList)

        //last step: sort them!
        //sorting is done by loft first
    }

    //to sort the shots, go through each loft and add the shots in a specific ordder that need to be fitted for
    let sortedShots = [] as LoftForFitting[];
    let shot: LoftForFitting | undefined;
    loftList.forEach((loft, lineNumber) => {
        shot = loftsForFitting.find(s => s.loft === loft.loft && s.shotType === 'Full');
        if (shot !== undefined) { sortedShots.push(shot); }
        shot = loftsForFitting.find(s => s.loft === loft.loft && s.shotType === 'Pitch');
        if (shot !== undefined) { sortedShots.push(shot); }
        shot = loftsForFitting.find(s => s.loft === loft.loft && s.shotType === 'Open Face');
        if (shot !== undefined) { sortedShots.push(shot); }
    });

    return sortedShots;
}

//add the empty shot to the collection of shots for fitting and don't allow dups
const addLoftToCollection = (shotsPerClub: number, lineNumber: number, loft: Loft, shotType: ShotSelection.ShotType, shotForFitting: LoftForFitting[]): boolean => {
    let found = false;
    let practiceShot = [] as ShotSelection.PracticeShot[];

    let thisLoft = shotForFitting.filter(s => s.loft === loft && s.shotType === shotType);
    if (thisLoft.length > 0) {
        found = true;
        //if the shot IS a duplicate, then make sure none of the shots are marked as 'active' b/c we need to reset the statuses before displaying on the form
        thisLoft[0].practiceShots.forEach(ps => ps.status = ps.data === undefined ? 'open' : 'closed');
    } else {
        //instantiate the shot and add the number of practice shots according to the fitter's preference
        for (let ix = 1; ix <= shotsPerClub; ix++) {
            practiceShot.push(new ShotSelection.PracticeShot("open"));
        }
        //add shot to collection
        shotForFitting.push(new LoftForFitting(lineNumber, loft, shotType, practiceShot));
    }
    return found;
}

//given a shot type, find out if it isn't in the shot fitting collection yet. If not, find the highest loft that uses it and add it to the collection
const assignHighestLoftForShot = (fitter: Fitter, shotType: ShotSelection.ShotType, loftsForFitting: LoftForFitting[], selectedLofts: LoftShots[]): void => {
    let shotFound = loftsForFitting.filter(loft => loft.shotType === shotType).length > 0;
    if (!shotFound) {
        //shot isn't in the collection, get list of all lofts that use it
        let lofts = selectedLofts.map(loft => {
            if (loft.shots.filter(shot => shot.shotType === shotType && shot.selected).length > 0) {
                return Number(loft.loft)
            } else {
                return 0;  //fixes a TS warning re having to return a value
            }
        });
        //take all lofts that use this shot, and use the highest one
        if (lofts.length > 0) {
            let highestLoftNumber = Math.max(...lofts);
            if (highestLoftNumber > 0) {
                let lineNumber = 0;
                let highestLoft = selectedLofts.find(loft => Number(loft.loft) === highestLoftNumber);
                if (highestLoft){
                    lineNumber = highestLoft.lineNumber;
                }
                addLoftToCollection(fitter.shotsPerClub, lineNumber, String(highestLoftNumber) as Loft, shotType, loftsForFitting);
            }
        }
    }
}

export const createEnhancedFittingData = async (loft: Loft, shotType: ShotSelection.ShotType, shotData: SimulatorData, fitter: Fitter, simulator: ISimulator, grindRecommendations: LoftGrindRecommendation[], msg: string) => {
    let aa_sl = simulator.calculateAttackAngleShaftLean(loft, shotType, shotData.attackAngle, shotData.dynamicLoft, false, true);
    let enhancedData = new EnhancedFittingData(shotData.Id, new Date().toISOString(), "false", loft, shotType, aa_sl.attackAngle, aa_sl.shaftLean, shotData.ballSpeed.toString(), shotData.launchAngle.toString(), grindRecommendations, msg);
    return enhancedData;
}

export const logout = async () => {
    try {
        await axiosInstance.post(
            `${process.env.REACT_APP_SECURE_API_GATEWAY}/auth/logout`,
            {}
        );
    } catch (err) {
        TagManager.dataLayer({
            dataLayer: {
                event: 'log_error',
                description: 'Golfer Helper - logout',
                msg: 'catch: ' + err
            }
        });
    } finally {
        await IdbKeyVal.del(GOLFER_ID);
    }
}
